import { atom } from "jotai";
import type { AtomContainer } from "@/state";

// Derived caches of known values across all models + templates, used to power
// <datalist> autocomplete in the add/edit dialogs. Every set is rebuilt from
// scratch on every read — that's fine because the input data is the
// already-cached Firestore result, and the cost is tiny (a few hundred entries).

const addAll = <T>(target: Set<T>, source: Iterable<T>): void => {
  for (const item of source) {
    if (item) {
      target.add(item);
    }
  }
};

export class SuggestionsAtoms {
  constructor(private readonly atoms: AtomContainer) {}

  // Series names: "Model Series 6500", "Model Series 51DV", …
  public seriesNames = atom<string[]>((get) => {
    const out = new Set<string>();
    addAll(
      out,
      Object.values(get(this.atoms.data.models))
        .map((m) => m.series)
        .filter(Boolean),
    );
    return [...out].sort();
  });

  // Product categories: "Pattern Diffusers - Steel", …
  public categories = atom<string[]>((get) => {
    const out = new Set<string>();
    addAll(
      out,
      Object.values(get(this.atoms.data.models))
        .map((m) => m.category)
        .filter((c): c is string => Boolean(c)),
    );
    return [...out].sort();
  });

  // Field names that have ever appeared on a template: "Frame Type", "Finish", …
  public fieldNames = atom<string[]>((get) => {
    const out = new Set<string>();
    for (const t of get(this.atoms.data.templates)) {
      addAll(out, t.fields.map((f) => f.name).filter(Boolean));
    }
    return [...out].sort();
  });

  // Every code that has appeared as an enum value, anywhere.
  public allCodes = atom<string[]>((get) => {
    const out = new Set<string>();
    for (const t of get(this.atoms.data.templates)) {
      for (const f of t.fields) {
        if (f.codes) {
          addAll(out, Object.keys(f.codes));
        }
      }
      if (t.accessories) {
        addAll(
          out,
          t.accessories.map((a) => a.code),
        );
      }
    }
    return [...out].sort();
  });

  // Codes grouped by the field name they appear under. So when the user types
  // a code into "Frame Type", we can suggest just the codes that other "Frame
  // Type" fields use — much higher signal than the global allCodes list.
  public codesByFieldName = atom<Record<string, string[]>>((get) => {
    const map = new Map<string, Set<string>>();
    for (const t of get(this.atoms.data.templates)) {
      for (const f of t.fields) {
        if (!f.codes) {
          continue;
        }
        let bucket = map.get(f.name);
        if (!bucket) {
          bucket = new Set();
          map.set(f.name, bucket);
        }
        addAll(bucket, Object.keys(f.codes));
      }
    }
    const out: Record<string, string[]> = {};
    for (const [name, set] of map) {
      out[name] = [...set].sort();
    }
    return out;
  });

  // The meanings each code has been given. Lets the meaning input suggest the
  // existing definition when the user types a code that's already known —
  // matters for keeping "AW" → "Appliance White" consistent across templates.
  public meaningsByCode = atom<Record<string, string[]>>((get) => {
    const map = new Map<string, Set<string>>();
    const remember = (code: string, meaning: string) => {
      if (!code || !meaning) {
        return;
      }
      let bucket = map.get(code);
      if (!bucket) {
        bucket = new Set();
        map.set(code, bucket);
      }
      bucket.add(meaning);
    };
    for (const t of get(this.atoms.data.templates)) {
      for (const f of t.fields) {
        if (f.codes) {
          for (const [code, meaning] of Object.entries(f.codes)) {
            remember(code, meaning);
          }
        }
      }
      for (const a of t.accessories ?? []) {
        remember(a.code, a.meaning);
      }
    }
    const out: Record<string, string[]> = {};
    for (const [code, set] of map) {
      out[code] = [...set].sort();
    }
    return out;
  });

  // Accessory categories: "Transition Collar", "Earthquake Tabs", …
  public accessoryCategories = atom<string[]>((get) => {
    const out = new Set<string>();
    for (const t of get(this.atoms.data.templates)) {
      addAll(out, (t.accessories ?? []).map((a) => a.category).filter(Boolean));
    }
    return [...out].sort();
  });

  // Catalog page tokens already used (e.g. "D81") — for the catalog_page input.
  public catalogPages = atom<string[]>((get) => {
    const out = new Set<string>();
    for (const t of get(this.atoms.data.templates)) {
      if (t.catalog_page) {
        out.add(t.catalog_page);
      }
    }
    return [...out].sort();
  });
}
